import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { LAYOUT, clampChatWidth } from '../layoutFit';

const WIDTH_KEY = 'studio-side-panel-width';

/**
 * The shell's right-hand panel column: whether it is open and how wide the user
 * dragged it.
 *
 * Named for its place in the layout rather than for the assistant that will live
 * in it. The column is a layout concern with no opinion about its contents, and
 * naming it `chat` would mean the next thing that wants a docked panel either
 * builds a second one or lives inside something called chat. Same reasoning that
 * kept `ai` out of the flag name.
 */

function readWidth(): number {
  try {
    const raw = window.localStorage.getItem(WIDTH_KEY);
    if (!raw) return LAYOUT.CHAT_DEFAULT_PX;
    const parsed = Number(raw);
    // A corrupted value must not size the panel. `Number('')` is 0, which would
    // clamp to the minimum and look deliberate.
    if (!Number.isFinite(parsed) || parsed <= 0) return LAYOUT.CHAT_DEFAULT_PX;
    // Re-clamped against THIS window: a width saved on a wide monitor must not
    // survive intact onto a laptop, where it would fold the panel to the rail.
    return clampChatWidth(parsed, window.innerWidth);
  } catch {
    return LAYOUT.CHAT_DEFAULT_PX;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Nothing here is worth failing a render over.
  }
}

interface SidePanelStoreValue {
  /** The user's chosen width. The shell may still render it as a rail. */
  width: number;
  /**
   * Whether anything on the current page wants the column at all.
   *
   * The shell cannot render a host for a panel and then decide whether a panel
   * exists, because the thing that would fill it renders far below and needs the
   * host to already be there. So the occupant announces itself on mount and the
   * shell reserves the column in response. Off this route, no column.
   */
  requested: boolean;
  /**
   * Where the occupant portals itself. `null` until the shell has mounted the
   * column, and null for good when the responsive shell is off, which is what
   * the occupant reads to decide whether to fall back to its own layout.
   */
  hostEl: HTMLElement | null;
  /**
   * Folded away. Latched rather than derived: the shell sets it when the window
   * runs out of room and the user sets it from the panel's own close control or
   * the header's Ask AI button, and only a click on Ask AI clears it.
   *
   * It lives here rather than in the shell because both ends need it. The shell
   * decides when there is no room, and the occupant draws the close button, and
   * the occupant renders far below the shell with no other way to reach it.
   */
  railed: boolean;
  /** Live width during a drag. Deliberately does not touch storage. */
  setWidth: (width: number) => void;
  /**
   * Write the current width to storage. Called once when a drag ends.
   *
   * Split from `setWidth` because that runs on every pointermove: persisting
   * there meant a synchronous `localStorage.setItem` per frame for the whole
   * gesture, to save sixty intermediate widths nobody wants.
   */
  persistWidth: () => void;
  setRequested: (requested: boolean) => void;
  setHostEl: (el: HTMLElement | null) => void;
  setRailed: (railed: boolean) => void;
}

const SidePanelContext = createContext<SidePanelStoreValue | null>(null);

export function SidePanelProvider({ children }: { children: ReactNode }) {
  // Read lazily rather than in a module constant: this file is imported by the
  // server bundle too, where there is no `window` at all.
  const [width, setWidthState] = useState(() =>
    typeof window === 'undefined' ? LAYOUT.CHAT_DEFAULT_PX : readWidth()
  );
  const [requested, setRequestedState] = useState(false);
  const [hostEl, setHostElState] = useState<HTMLElement | null>(null);
  const [railed, setRailedState] = useState(false);

  const setWidth = useCallback((next: number) => {
    // Clamped against the CURRENT viewport, not a flat cap: on a large screen the
    // binding constraint is the content floor, which leaves far more room than 560.
    setWidthState(
      clampChatWidth(
        next,
        typeof window === 'undefined' ? undefined : window.innerWidth
      )
    );
  }, []);

  const persistWidth = useCallback(() => {
    setWidthState((current) => {
      write(WIDTH_KEY, String(current));
      return current;
    });
  }, []);

  // Both stable, because the occupant sets them from effects and a changing
  // identity would re-run those on every render.
  const setRequested = useCallback((next: boolean) => {
    setRequestedState(next);
  }, []);

  const setHostEl = useCallback((el: HTMLElement | null) => {
    setHostElState(el);
  }, []);

  const setRailed = useCallback((next: boolean) => {
    setRailedState(next);
  }, []);

  const value = useMemo<SidePanelStoreValue>(
    () => ({
      width,
      requested,
      hostEl,
      railed,
      setWidth,
      persistWidth,
      setRequested,
      setHostEl,
      setRailed,
    }),
    [
      width,
      requested,
      hostEl,
      railed,
      setWidth,
      persistWidth,
      setRequested,
      setHostEl,
      setRailed,
    ]
  );

  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  );
}

export function useSidePanelStore(): SidePanelStoreValue {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error('useSidePanelStore must be used within SidePanelProvider');
  }
  return context;
}
